/**
 * clientId.js - one id per browser tab.
 *
 * src/index.js sends it on every /api request as X-Client-Id, and the server
 * puts it on the live event that request causes (server/lib/liveEvents.js).
 * The tab that made a change already has it on screen, so liveEvents.js skips
 * events carrying this tab's own id - reloading underneath the person who just
 * saved would only be noise.
 *
 * Kept in sessionStorage, so a reload of the same tab keeps its id. Only a
 * RELOAD reuses it: "Duplicate tab" copies sessionStorage too, and two tabs
 * sharing one id would each skip the other's changes and quietly go stale.
 */
const KEY = 'globalops:clientId';

const fresh = () => {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
  } catch {
    /* not a secure context - fall through */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

const wasReload = () => {
  try {
    const nav = performance.getEntriesByType('navigation')[0];
    return !!nav && nav.type === 'reload';
  } catch {
    return false;
  }
};

/** This tab's id, read once. Exported for the tests. */
export function makeClientId(storage, reloaded = wasReload()) {
  try {
    const store = storage || window.sessionStorage; // reading it can throw when storage is blocked
    const kept = store.getItem(KEY);
    if (kept && reloaded && /^[A-Za-z0-9-]{1,64}$/.test(kept)) return kept;
    const id = fresh();
    store.setItem(KEY, id);
    return id;
  } catch {
    return fresh(); // storage blocked: still one id for this page
  }
}

export const CLIENT_ID = makeClientId();
